import React from 'react'
import { Observable } from 'rx'
import type { Selection, FobSelection, EventDetails } from '@mobi/betslip/types'
import {
  isFobSelection,
  isNoveltyBetType,
  isRaceDetails,
  isSameRaceMultiSelection,
  isStartingPriceSelection,
  isToteSelection,
} from '@mobi/betslip/helpers/typeGuards'
import { Currency } from '@mobi/utils/money'
import { observeImmutable } from '@core/Components/HOCs'
import { state$ as quickbetState$, QuickbetState } from '../../driver'
import { state$ as superPickState$, SuperPickState } from '../SuperPick/Components/SuperPick/driver'
import { InvestmentComponentProps } from '../BetInvestment/BetInvestment'
import {
  LegInfoWrapperStyled,
  SingleLegTypeStyled,
  SingleLegPriceStyled,
  EachWayTypeStyled,
  EachWayPriceStyled,
  SingleLegMoneyGroupedStyled,
  LegInfoPriceTextStyled,
} from './LegInfo.styles'
import { getPriceChange, PriceChange } from '@core/Components/Text/utils'
import { BetSpecialOffer } from '@classic/Specials/Model/BetSpecialOffer'
import { AnimatedNumberTransition } from '@mobi/component-library/Common/AnimatedNumberTransition'
import { calculateMultiReturn } from '@mobi/betslip/helpers/calculator/multis'
import { roundDownDecimal } from '@mobi/betslip/helpers/calculator/misc'

interface LegInfoState {
  selection: Selection | null
  selectionDetails: EventDetails
  selectionMultiLegs: QuickbetState['selectionMultiLegs']
  winPricePreviousAndCurrent: number[]
  placePricePreviousAndCurrent: number[]
  selectedSuperPickOffer?: BetSpecialOffer | null
  isEachWay: boolean
}

type LegInfoProps = Pick<InvestmentComponentProps, 'investmentType' | 'isActive'>

export type LegInfoCombinedProps = LegInfoState & LegInfoProps

export const LegInfoComponent: React.FC<LegInfoCombinedProps> = ({
  selection,
  selectionDetails,
  selectionMultiLegs,
  isActive,
  isEachWay,
  selectedSuperPickOffer,
  winPricePreviousAndCurrent,
  placePricePreviousAndCurrent,
  investmentType,
}) => {
  const isMultiSelection = selectionMultiLegs && selectionMultiLegs.length > 0
  if (isMultiSelection) {
    const winPriceChange = winPricePreviousAndCurrent
      ? getPriceChange(winPricePreviousAndCurrent[0], winPricePreviousAndCurrent[1])
      : PriceChange.None
    return (
      <LegInfoWithPriceComponent
        legType='Multi'
        price={roundDownDecimal(calculateMultiReturn(selectionMultiLegs, { shouldRound: true }))}
        isActive={isActive}
        boostAmount={0}
        priceChange={winPriceChange}
      />
    )
  }

  let winBoost = 0
  let placeBoost = 0

  if (selectedSuperPickOffer) {
    const elementWithPriceIncrease = selectedSuperPickOffer.elements?.find(
      element => element.priceIncrease !== null
    )
    if (elementWithPriceIncrease) {
      winBoost = elementWithPriceIncrease.priceIncrease?.win.toNumber() as number
      placeBoost = elementWithPriceIncrease.priceIncrease?.place.toNumber() as number
    }
  }

  if (isStartingPriceSelection(selection)) {
    return <LegInfoStartingPriceComponent legType='Win' isActive={isActive} />
  }

  if (isFobSelection(selection)) {
    const winPriceChange = winPricePreviousAndCurrent
      ? getPriceChange(winPricePreviousAndCurrent[0], winPricePreviousAndCurrent[1])
      : PriceChange.None
    const placePriceChange = placePricePreviousAndCurrent
      ? getPriceChange(placePricePreviousAndCurrent[0], placePricePreviousAndCurrent[1])
      : PriceChange.None

    if (isRaceDetails(selectionDetails)) {
      if (investmentType === 'win') {
        const legType = isSameRaceMultiSelection(selection) ? 'Same Race Multi' : 'Win'
        return (
          <LegInfoWithPriceComponent
            legType={legType}
            price={selection.winPrice}
            isActive={isActive}
            boostAmount={winBoost}
            priceChange={winPriceChange}
          />
        )
      }
      if (investmentType === 'place') {
        return (
          <LegInfoWithPriceComponent
            legType='Place'
            price={selection.placePrice as number}
            isActive={isActive}
            boostAmount={placeBoost}
            priceChange={placePriceChange}
          />
        )
      }
    }

    if (isEachWay) {
      return (
        <LegInfoWrapperStyled data-testid='LegInfo.EachWay'>
          <EachWayTypeStyled isActive={isActive}>
            Win <LegInfoPriceTextStyled>price:</LegInfoPriceTextStyled>
          </EachWayTypeStyled>
          <EachWayPriceStyled>
            <SingleLegMoneyGroupedStyled
              data-tid='qb-win-price'
              price={selection.winPrice}
              priceChange={winPriceChange}
              isActive={isActive}
              shouldHighlight={false}
            />
          </EachWayPriceStyled>
          <EachWayTypeStyled isActive={isActive}>
            Place <LegInfoPriceTextStyled>price:</LegInfoPriceTextStyled>
          </EachWayTypeStyled>
          <EachWayPriceStyled>
            <SingleLegMoneyGroupedStyled
              data-tid='qb-place-price'
              price={selection.placePrice as number}
              priceChange={placePriceChange}
              isActive={isActive}
              shouldHighlight={false}
            />
          </EachWayPriceStyled>
        </LegInfoWrapperStyled>
      )
    }
    if (investmentType === 'win') {
      return (
        <LegInfoWithPriceComponent
          legType='Win'
          price={selection.winPrice}
          isActive={isActive}
          boostAmount={winBoost}
          priceChange={winPriceChange}
        />
      )
    }
    if (investmentType === 'place') {
      return (
        <LegInfoWithPriceComponent
          legType='Place'
          price={selection.placePrice as number}
          isActive={isActive}
          boostAmount={placeBoost}
          priceChange={placePriceChange}
        />
      )
    }
  }

  if (isToteSelection(selection)) {
    let label = investmentType === 'win' ? 'Win' : 'Place'
    if (isNoveltyBetType(selection.betType) || selection.betType === 'All Up') {
      label = 'Spend'
    }
    return (
      <LegInfoWrapperStyled>
        <SingleLegTypeStyled data-testid='LegInfo.SingleLeg' isActive={isActive}>
          {label}
        </SingleLegTypeStyled>
      </LegInfoWrapperStyled>
    )
  }
  return null
}

const state$ = Observable.combineLatest(
  quickbetState$,
  quickbetState$
    .map(record => {
      const quickbetState = record.toJS() as QuickbetState
      if (quickbetState.selectionMultiLegs) {
        const multiReturn = roundDownDecimal(
          calculateMultiReturn(quickbetState.selectionMultiLegs, { shouldRound: true })
        )
        return multiReturn
      }
      return (quickbetState.selection as FobSelection).winPrice
    })
    .pairwise()
    .filter(isPriceUpdated)
    .startWith([0, 0]),
  quickbetState$
    .map(record => {
      const quickbetState = record.toJS() as QuickbetState
      if (quickbetState.selectionMultiLegs) return 0
      return (quickbetState.selection as FobSelection).placePrice
    })
    .pairwise()
    .filter(isPriceUpdated)
    .startWith([0, 0]),
  superPickState$,
  (quickbetStateRecord, winPriceHistory, placePriceHistory, superPickStateRecord): LegInfoState => {
    const superPick: SuperPickState = superPickStateRecord.toJS()
    const specialOffers = superPick.specialOffers
    const selectedSuperPickOffer = specialOffers
      ? specialOffers.find(offer => offer.specialSeq === superPick.selectedSuperPickSeq)
      : null
    const quickbet: QuickbetState = quickbetStateRecord.toJS()

    return {
      selection: quickbet.selection as Selection,
      selectionDetails: quickbet.selectionDetails as EventDetails,
      selectionMultiLegs: quickbet.selectionMultiLegs,
      isEachWay: quickbet.isEachWay,
      winPricePreviousAndCurrent: winPriceHistory,
      placePricePreviousAndCurrent: placePriceHistory as number[],
      selectedSuperPickOffer,
    }
  }
)

export const LegInfo: React.ComponentClass<LegInfoProps> = observeImmutable<
  LegInfoState,
  LegInfoProps
>(state$, ({ record, investmentType, isActive }) => (
  <LegInfoComponent {...record} investmentType={investmentType} isActive={isActive} />
))

LegInfo.displayName = 'LegInfo'

// ================
// Local Components
// ================

const LegInfoWithPriceComponent: React.FC<{
  legType: string
  price: number
  isActive: boolean
  boostAmount: number
  priceChange: PriceChange
}> = ({ legType, price, isActive = true, boostAmount = 0, priceChange = PriceChange.None }) => {
  const isBoosted = boostAmount > 0
  const finalPrice = new Currency(price || 0).add(boostAmount).format()

  return (
    <LegInfoWrapperStyled>
      <SingleLegTypeStyled data-testid='LegInfo.SingleLeg' isActive={isActive}>
        {legType}{' '}
        <LegInfoPriceTextStyled>price{isBoosted ? ' boosted' : null}:</LegInfoPriceTextStyled>
      </SingleLegTypeStyled>

      <SingleLegPriceStyled
        data-tid={`qb-${legType.toLowerCase()}-price`}
        shouldHighlight={isBoosted}
        isActive={isActive}
        priceChange={priceChange}
      >
        {isBoosted ? (
          <AnimatedNumberTransition start={new Currency(price || 0).format()} end={finalPrice} />
        ) : (
          finalPrice
        )}
      </SingleLegPriceStyled>
    </LegInfoWrapperStyled>
  )
}

const LegInfoStartingPriceComponent: React.FC<{
  legType: string
  isActive: boolean
}> = ({ legType, isActive = true }) => {
  return (
    <LegInfoWrapperStyled data-testid='leg-info-sp'>
      <SingleLegTypeStyled isActive={isActive}>{legType} </SingleLegTypeStyled>

      <SingleLegPriceStyled
        shouldHighlight={false}
        isActive={isActive}
        priceChange={PriceChange.None}
      >
        SP
      </SingleLegPriceStyled>
    </LegInfoWrapperStyled>
  )
}

// =============
// Local Helpers
// =============

function isPriceUpdated(pair: (number | null)[]): boolean {
  return pair[0] !== pair[1]
}
